import { codeUnitString } from "./code-unit-string.js";
import { RESOURCE_SLUG_PATTERN } from "./slug-grammar.js";
import { CHANNEL_KEY_MESSAGE, isPrivateChannelKey, isValidChannelKey } from "./channel-grammar.js";
import { z } from "zod";
//#region src/machine-principal.ts
/**
* Machine principal — a non-human actor (agent / worker / service), OR a human
* enrolled as a `machine_principals` row (`human`, M5) so it becomes
* broker-resolvable through the identical stream path. A first-class platform
* principal, identical to a human in every way except its credential (machine
* identity plan §2.2, D1).
*
* A machine principal authenticates through the DP machine auth provider
* (RFC 7523 assertion), receives the same `data_plane` platform JWT a human
* receives, and is authorized through the same RBAC — there is no bespoke
* agent-authorization path. The DP `auth.machine_principals` table is the
* system of record; this is the wire/contract shape consumers see.
*
* Imports: `zod`, and the code-unit string builder, the channel grammar and the resource-slug
* grammar of this package, and nothing else. The runtime exports are
* pure functions over the `attributes` map, frozen literals, the admin route constants and the zod
* schemas of the machine-principal admin surface (2026-10-02-directory-asks-platform, I1 to I4).
*/
/**
* The attribute key of the host mark (I-12). On a host row, a `service` principal the operator
* enrols for a `multiOrgAgentHost` fixture, its value is one of {@link AGENT_HOST_KINDS}.
*
* The same key carries an older value on another kind of row. team-hosting D-003 (2026-09-27) marks
* an `agent`-type principal that hosts several minds with the literal `'true'`, set at enrol by a
* caller holding `platform:machine-principal:enroll-host`; core-api's NATS grant shape reads that
* literal and nothing else. `'true'` is not a host kind: {@link agentHostKindOf} answers `null` for
* it, so a D-003 marker never makes its row an issuer.
*/
const AGENT_HOST_ATTRIBUTE = "agentHost";
/**
* The host kinds a host mark names, in a fixed order. `platform` is the platform's own agent
* runtime, the anchor id from the chart value `agentRuntime.principalId`; it issues across orgs as
* team-hosting D-004 ruled. `app` is an app's runtime enrolled under its per-host id (I-11); it
* issues only for agents bound to it, inside its own org (I-13).
*/
const AGENT_HOST_KINDS = Object.freeze(["platform", "app"]);
/**
* The attribute key of an agent's host binding (I-13, I-14). On an `agent` row its value is the
* `machine_principals.id` of the host allowed to mint the agent's bearer, as a lower-case UUID.
* core-api derives it at enrol: the host id of the enroller's app when the caller is that app's
* machine enroller, or the host the operator enrols for. No request body supplies it.
*
* The same key once named the host-identity substitution target on a compute-unit row (the
* operator's compute-unit broker, WS-D D17). core-api removed host substitution (U12, R-054), and
* the 40.0.0 major removed the broker. A compute-unit row can still hold the key, and no
* substitution reads it there.
*/
const AGENT_HOST_PRINCIPAL_ID_ATTRIBUTE = "agentHostPrincipalId";
/** The 8-4-4-4-12 hex shape in lower case, with no version or variant check. */
const LOWER_CASE_UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** The zod UUID check, the one `machinePrincipalRecordSchema.principalId` and core-api's `:id` pipe apply. */
const UUID_CHECK = z.uuid();
/**
* True when `id` is a lower-case UUID that zod's `z.uuid()` admits: the 8-4-4-4-12 hex shape in
* lower case, with a version nibble of 1 to 8 and a variant nibble of 8, 9, a or b, or the nil or
* the max UUID. This is the stored form of a platform row id (dp-resource-grants R-045) and the only
* form core-api's machine-principal `{id}` guard admits (dp-resource-grants R-016).
*
* Every other value answers `false`: a non-string, an upper-case or mixed-case UUID, a braced UUID,
* a version nibble of 0 or 9 to f, a variant nibble outside 8, 9, a and b, a truncated or extended
* string, and any string with another byte. Callers: {@link machinePrincipalPath},
* {@link agentHostPrincipalIdOf}, and sdk-node's `MachinePrincipalsClient` and
* `FakeMachinePrincipalsClient`. Pure; never throws.
*/
function isLowerCaseUuid(id) {
	return typeof id === "string" && LOWER_CASE_UUID_SHAPE.test(id) && UUID_CHECK.safeParse(id).success;
}
/**
* The three attribute keys no request may name: `__proto__`, `constructor` and `prototype`. An
* assignment `map[key] = value` with one of these keys reaches the prototype chain of the map, so
* every attribute schema refuses `constructor` and `prototype` with
* {@link RESERVED_ATTRIBUTE_KEY_MESSAGE} (D-03), and every attribute merge skips all three. zod 4
* (4.4.3, `$ZodRecord` and `$ZodObject`) drops an own `__proto__` key before any check runs, so no
* schema sees that key and the parsed value never holds it. Callers: `updateMachinePrincipalRequestSchema` here, the group
* attribute schemas and `applyGroupAttributesPatch` in `@zion/sdk-dp-admin-contracts`, and
* core-api's machine-principal merge.
*/
const RESERVED_ATTRIBUTE_KEYS = Object.freeze([
	"__proto__",
	"constructor",
	"prototype"
]);
/** The 400 message for an attribute key in {@link RESERVED_ATTRIBUTE_KEYS}. */
const RESERVED_ATTRIBUTE_KEY_MESSAGE = "an attribute key may not be __proto__, constructor or prototype";
/**
* True when `key` is one of {@link RESERVED_ATTRIBUTE_KEYS}. The match is exact and case-sensitive:
* `Constructor` and `a.__proto__` answer `false`. Pure; never throws.
*/
function isReservedAttributeKey(key) {
	return RESERVED_ATTRIBUTE_KEYS.includes(key);
}
/** The value under `key` when `attributes` owns it as a string; `undefined` otherwise. */
function ownString(attributes, key) {
	if (attributes === null || attributes === void 0 || typeof attributes !== "object") return void 0;
	if (!Object.hasOwn(attributes, key)) return void 0;
	const value = attributes[key];
	return typeof value === "string" ? value : void 0;
}
/**
* The host kind a row's attributes mark, or `null` when the row is not a host.
*
* Returns `'platform'` or `'app'` only when `attributes.agentHost` is an own string property equal to
* that literal. Every other input answers `null`: no attributes, no key, an inherited key, a
* non-string value, the D-003 literal `'true'`, and any case variant such as `'App'`.
*
* Callers: core-api's issuance (WS-6), which admits an app host as an issuer only when this answers
* `'app'` for the caller's own principal row, and the operator's tests (WS-7). Pure; never throws.
*/
function agentHostKindOf(attributes) {
	const value = ownString(attributes, AGENT_HOST_ATTRIBUTE);
	return AGENT_HOST_KINDS.find((kind) => kind === value) ?? null;
}
/**
* The host binding a row's attributes carry, or `null` when the row names no host.
*
* Returns the value of `attributes.agentHostPrincipalId` only when it is an own string property in
* the lower-case UUID form core-api stores. Every other input answers `null`: no attributes, no
* key, an inherited key, a non-string value, an upper-case or braced UUID, and any other string.
* A malformed binding binds the agent to no host, so an app host is refused for it.
*
* Callers: core-api's issuance (WS-6), which compares the answer for the target agent's row with
* the issuer's `sub`, and WS-2's enrol tests. Pure; never throws.
*/
function agentHostPrincipalIdOf(attributes) {
	const value = ownString(attributes, AGENT_HOST_PRINCIPAL_ID_ATTRIBUTE);
	return value !== void 0 && isLowerCaseUuid(value) ? value : null;
}
/**
* The operator-only agent-host attributes a request body names, in the order
* `[agentHost, agentHostPrincipalId]`, or an empty list when it names neither.
*
* - `agentHost` counts when its value is a host kind, `'platform'` or `'app'`, compared as the exact
*   string. Every other value, the D-003 literal `'true'` and a near-miss such as `'App'` included,
*   stays under D-003's own presence check in core-api's enrol, which answers a caller without
*   `platform:machine-principal:enroll-host` 403 whatever the value. A near-miss an `enroll-host`
*   holder stores marks no host: {@link agentHostKindOf} answers `null` for it.
* - `agentHostPrincipalId` counts whenever the body owns the key, whatever the value, so a caller
*   cannot probe with a deliberately invalid id.
*
* Only the operator caller class writes these two attributes (I-12, I-14). core-api's enrol (WS-2)
* refuses a body from any other caller class for which this list is non-empty, with 400 naming the
* keys, before any side effect (R-001). A `computeUnit` key beside `agentHostPrincipalId` changes
* nothing: core-api has no compute-unit 403 since the 40.0.0 major, so the 400 answers. `null`,
* `undefined` and a non-object answer an empty list. Pure; never throws.
*/
function operatorOnlyAgentHostAttributesIn(attributes) {
	if (attributes === null || attributes === void 0 || typeof attributes !== "object") return [];
	const named = [];
	if (agentHostKindOf(attributes) !== null) named.push(AGENT_HOST_ATTRIBUTE);
	if (Object.hasOwn(attributes, "agentHostPrincipalId")) named.push(AGENT_HOST_PRINCIPAL_ID_ATTRIBUTE);
	return named;
}
/**
* The lifecycle statuses of a machine principal, in a fixed order, frozen. The order is the order
* of the `machine_principals_status_check` array in core-api's migration 0054 (ruling R-001).
*
* - `active`: the principal mints, rotates, logs in and receives NATS credentials.
* - `revoked`: terminal. `POST /internal/v1/machine-principals/{id}/revoke` sets it, and no route
*   returns the principal to `active`.
* - `suspended`: reversible (I3). A `PATCH` with `status: 'suspended'` sets it and a `PATCH` with
*   `status: 'active'` clears it. Every door that refuses a non-active principal refuses a
*   suspended one, with a message that names the state (D-09). A token minted before the suspend
*   lives to its own `exp`.
*/
const MACHINE_PRINCIPAL_STATUSES = Object.freeze([
	"active",
	"revoked",
	"suspended"
]);
/**
* The statuses a `PATCH` may set: `active` and `suspended`. `revoked` is absent, because a revoke
* is terminal and has its own route with its own audit row. Frozen.
*/
const MACHINE_PRINCIPAL_SETTABLE_STATUSES = Object.freeze(["active", "suspended"]);
/**
* The values of {@link MachinePrincipalType}, in its declaration order, for a `z.enum` at a
* boundary: `agent`, `worker`, `service`, `human` and `external`. The `satisfies` clause makes the
* typecheck fail when a member names no type. Frozen.
*
* `external` is a member since the merge of dp-oidc-federation (ruling R-002): the admin surface
* lists, reads and updates an `external` row, and core-api's role guard for external principals
* decides its `PATCH` `roles`. `human` stays a member so that the record type covers every row
* kind, but the admin surface answers no `human` row (ruling R-004).
*/
const MACHINE_PRINCIPAL_TYPES = Object.freeze([
	"agent",
	"worker",
	"service",
	"human",
	"external"
]);
/**
* The attribute keys that the admin `PATCH` refuses from every caller, in a fixed order, frozen:
* `agentHost`, `agentHostPrincipalId` and `computeUnit` (ruling R-010).
*
* - `agentHost` is {@link AGENT_HOST_ATTRIBUTE}, the host mark (I-12).
* - `agentHostPrincipalId` is {@link AGENT_HOST_PRINCIPAL_ID_ATTRIBUTE}, the host binding (I-14).
* - `computeUnit` is the compute-unit marker. The operator's compute-unit broker wrote it, and the
*   40.0.0 major removed the broker. Rows that the broker enrolled still carry the key, and no
*   reader in core-api decides on it.
*
* The list is a `PATCH` rule and no enroll rule. core-api's enrol refuses `agentHost` and
* `agentHostPrincipalId` from a JWT caller through {@link operatorOnlyAgentHostAttributesIn}. Since
* the 40.0.0 major the enrol accepts and stores `computeUnit` from a JWT caller (plan
* 2026-10-14-app-pools-runtime-deletion, R-081 item 4). So a row that holds `computeUnit` can come
* from a tenant enrol, and the key proves no operator write.
*
* Every caller of the admin surface is a JWT caller. So core-api (WS-2) refuses a `PATCH` whose
* attribute patch owns one of these keys with 400, whatever the value: a string sets the key and
* `null` deletes it, and both are refused. The refusal comes before any read or write. sdk-node's
* `FakeMachinePrincipalsClient` (WS-6) refuses the same keys. Both import this list and spell no
* literal of their own.
*/
const MACHINE_PRINCIPAL_OPERATOR_ONLY_ATTRIBUTE_KEYS = Object.freeze([
	AGENT_HOST_ATTRIBUTE,
	AGENT_HOST_PRINCIPAL_ID_ATTRIBUTE,
	"computeUnit"
]);
/** The page size `GET /internal/v1/machine-principals` uses when the query names no `limit`. */
const MACHINE_PRINCIPALS_DEFAULT_PAGE = 100;
/** The largest `limit` the list query admits; a larger one is 400. The operator list uses 500 too. */
const MACHINE_PRINCIPALS_MAX_PAGE = 500;
/** The longest opaque `cursor` the list query admits. The keyset token is far shorter. */
const MACHINE_PRINCIPALS_CURSOR_MAX_LENGTH = 512;
/** The longest `agentId` filter the list query admits. */
const MACHINE_PRINCIPAL_AGENT_ID_MAX_LENGTH = 256;
/**
* The `agentId` grammar that enroll refuses with a 400: any string that starts with `ZION_`, `KV_` or
* `OBJ_` (2026-10-05-stream-resource-permissions, overview D-3).
*
* A `nats_stream` resource id is either a principal slug (`machine_principals.agent_id`) or a platform
* stream name (`PLATFORM_STREAMS` in `platform-streams.ts`). Every platform stream name matches
* `PLATFORM_STREAM_NAME_PATTERN`, which starts with `ZION_`, so the refused prefix keeps every slug
* apart from every platform stream name, the ones registered later included. The pattern also refuses
* `KV_` and `OBJ_`, the prefixes of the JetStream streams behind a KV bucket and an object store, so
* no slug names such a stream either (`research/r2-type-split.md` section 1 keeps the pattern). The match is
* case-sensitive, as NATS stream names are: `kv_x` is a legal `agentId`.
*
* Caller: core-api's enroll schema (`machine-principal.dto.ts`). It reads nothing else and has no
* side effects.
*/
const AGENT_ID_RESERVED_PATTERN = /^(?:ZION|KV|OBJ)_/;
/**
* The longest `appSlug` filter the list query admits: 63, the app slug bound of
* `resource-grants.ts` and of the release-engine app slug (admin-redesign-asks `00-index.md` §3).
*/
const MACHINE_PRINCIPAL_APP_SLUG_MAX_LENGTH = 63;
/** The `displayName` bound, the same 256 the enroll body carries. */
const MACHINE_PRINCIPAL_DISPLAY_NAME_MAX_LENGTH = 256;
/** The most role slugs one `PATCH` may set. */
const MACHINE_PRINCIPAL_ROLES_MAX = 64;
/** The longest role slug a `PATCH` may name. */
const MACHINE_PRINCIPAL_ROLE_SLUG_MAX_LENGTH = 256;
/** The most entries one `PATCH` attribute patch may name (ruling R-029). The stored column has no cap. */
const MACHINE_PRINCIPAL_PATCH_ATTRIBUTES_MAX = 64;
/** The longest key one `PATCH` attribute patch may name, in UTF-16 code units (ruling R-029). */
const MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_KEY_MAX_LENGTH = 128;
/** The longest string value one `PATCH` attribute patch may set, in UTF-16 code units (ruling R-029). */
const MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_VALUE_MAX_LENGTH = 4096;
/** The attribute key that carries a principal's public release channel (#804). */
const CHANNEL_ATTRIBUTE_KEY = "channel";
/**
* The 400 message when a `PATCH` body sets `attributes.channel` to a private channel key `p-…`
* (private-release-channels R-019). The text is the enroll door's text.
*/
const MACHINE_PRINCIPAL_PRIVATE_CHANNEL_MESSAGE = "attributes.channel must be a public routing key; a machine principal cannot be enrolled on a private channel key (p-…)";
/**
* One machine principal as the admin surface answers it: the {@link MachinePrincipal} fields plus
* `agentId`, `roles` and `appSlug` (I4).
*
* - `principalId`: the `machine_principals.id`, any RFC UUID version (an operator-derived service
*   id is a v8 UUID). core-api stores and answers it in lower case.
* - `type`: one of {@link MACHINE_PRINCIPAL_TYPES}. A type this version does not list fails the
*   parse (D-01). core-api answers no `human` row on this surface (ruling R-004).
* - `orgId`: the DP-local organization id.
* - `agentId`: the stream slug, `null` for a row with none.
* - `displayName`, `attributes`: the row's columns. `attributes` is the open string map.
* - `roles`: the org role slugs the principal holds, in the order core-api reads them.
* - `status`: one of {@link MACHINE_PRINCIPAL_STATUSES}.
* - `appSlug`: the slug of the app the row belongs to: the stored `app_slug` (the app whose service
*   principal enrolled this row, I10), else the slug core-api derives from the row's `k8s_subject`
*   (`appSlugOf`, admin-redesign-asks D-8). `null` when neither names an app, such as a row an
*   operator enrolled. The `appSlug` filter of {@link listMachinePrincipalsQuerySchema} matches
*   this value.
* - `createdAt`: ISO-8601 with a `Z` offset, as `JSON.stringify` writes a `Date`.
*
* Direction: a response schema, `z.object`, so a key core-api adds later is stripped and is no
* refusal. Owner: WS-2's `MachinePrincipalsController`. Consumers: WS-6's `MachinePrincipalsClient`
* and its fake.
*/
const machinePrincipalRecordSchema = z.object({
	principalId: z.uuid(),
	type: z.enum(MACHINE_PRINCIPAL_TYPES),
	orgId: z.uuid(),
	agentId: z.string().nullable(),
	displayName: z.string(),
	attributes: z.record(z.string(), z.string()),
	roles: z.array(z.string()),
	status: z.enum(MACHINE_PRINCIPAL_STATUSES),
	appSlug: z.string().nullable(),
	createdAt: z.iso.datetime()
});
/**
* The body of `GET /internal/v1/machine-principals`: one page of principals in keyset order on the
* principal id, and `nextCursor`, the opaque token of the next page. `nextCursor` is present when
* another page follows and absent on the last page; an empty string is refused. The shape follows
* {@link ListActiveMachinePrincipalsResponse}.
*/
const machinePrincipalPageSchema = z.object({
	principals: z.array(machinePrincipalRecordSchema),
	nextCursor: z.string().min(1).max(512).optional()
});
/**
* The query of `GET /internal/v1/machine-principals`. Every key is optional and each one narrows
* the answer; the answer never holds a principal the caller does not manage (I2).
*
* - `type`: one of {@link MACHINE_PRINCIPAL_TYPES}. `type=human` parses, and core-api answers an
*   empty page for it, because the list holds no `human` row (ruling R-004).
* - `status`: one of {@link MACHINE_PRINCIPAL_STATUSES}.
* - `agentId`: an exact stream slug, 1 to {@link MACHINE_PRINCIPAL_AGENT_ID_MAX_LENGTH} characters.
* - `appSlug`: an exact app slug, 2 to {@link MACHINE_PRINCIPAL_APP_SLUG_MAX_LENGTH} characters of
*   `RESOURCE_SLUG_PATTERN` (`^[a-z0-9][a-z0-9-]*[a-z0-9]$`). The filter matches the derived slug
*   that the record's `appSlug` shows: the stored `app_slug` column, else the slug core-api derives
*   from the principal's `k8s_subject` (`appSlugOf`). So a service principal whose `app_slug`
*   column is NULL still matches its app (admin-redesign-asks C-3, D-8). core-api binds the value
*   as an SQL parameter. A record whose `appSlug` is `null` matches no `appSlug` filter.
* - `cursor`: the `nextCursor` of the previous page, 1 to
*   {@link MACHINE_PRINCIPALS_CURSOR_MAX_LENGTH} characters. core-api answers 400 for a token it
*   cannot decode.
* - `limit`: an integer from 1 to {@link MACHINE_PRINCIPALS_MAX_PAGE}, coerced from the query
*   string. Absent means {@link MACHINE_PRINCIPALS_DEFAULT_PAGE}.
*
* Strict: an unknown key, an `orgId` included, is refused, 400 on the server. The schema carries no
* `.transform()`, so `createOpenApiDocument()` renders it. Owner: WS-2's controller, through
* `@Query({ schema })`. Consumer: WS-6's `MachinePrincipalsClient.list`.
*/
const listMachinePrincipalsQuerySchema = z.strictObject({
	type: z.enum(MACHINE_PRINCIPAL_TYPES).describe("Only principals of this type").optional(),
	status: z.enum(MACHINE_PRINCIPAL_STATUSES).describe("Only principals in this status").optional(),
	agentId: z.string().min(1).max(256).describe("Only the principal with this agent id").optional(),
	appSlug: z.string().max(63).regex(RESOURCE_SLUG_PATTERN).describe("Only the principals whose appSlug is this app slug").optional(),
	cursor: z.string().min(1).max(512).describe("The nextCursor of the previous page").optional(),
	limit: z.coerce.number().int().min(1).max(500).describe("Page size, 1 to 500; 100 when absent").optional()
});
/**
* The attribute patch of a `PATCH`: a key with a string sets that key, a key with `null` deletes
* it, and a key the patch does not name keeps its stored value. The stored column stays an open
* bag (I7), and the route bounds one patch (ruling R-029): at most
* {@link MACHINE_PRINCIPAL_PATCH_ATTRIBUTES_MAX} entries, each key 1 to
* {@link MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_KEY_MAX_LENGTH} characters, each string value at most
* {@link MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_VALUE_MAX_LENGTH} characters. A patch over a bound is 400
* before any read. The bound reads the patch only, so the stored map can grow over several patches.
* There is no key grammar, except that `constructor` and `prototype` are 400 on `attributes` with
* {@link RESERVED_ATTRIBUTE_KEY_MESSAGE}. zod 4 drops an own `__proto__` key of any record before
* a check runs, so a `__proto__` key leaves the parsed patch and changes nothing. One key has a grammar, `channel`, the routing key four machine mints sign
* (#804): a string value must pass `isValidChannelKey` and must not be a private key `p-…`
* (private-release-channels R-019). A `null` `channel` deletes the key and passes.
*
* core-api (WS-2) refuses the keys of {@link MACHINE_PRINCIPAL_OPERATOR_ONLY_ATTRIBUTE_KEYS} from a
* JWT caller with 400, before any write. The enrol refuses two of the three keys, and accepts
* `computeUnit` (see {@link MACHINE_PRINCIPAL_OPERATOR_ONLY_ATTRIBUTE_KEYS}). A `null` on one of these keys is refused
* as a string is (ruling R-010). That rule reads the caller class, so it lives in the service and
* not in this schema.
*/
const machinePrincipalAttributesPatchSchema = z.record(codeUnitString().min(1).max(128), codeUnitString().max(MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_VALUE_MAX_LENGTH).nullable()).refine((attrs) => !Object.keys(attrs).some(isReservedAttributeKey), { message: RESERVED_ATTRIBUTE_KEY_MESSAGE }).refine((attrs) => Object.keys(attrs).length <= 64, { message: `an attribute patch names at most 64 keys` }).refine((attrs) => typeof attrs[CHANNEL_ATTRIBUTE_KEY] !== "string" || isValidChannelKey(attrs[CHANNEL_ATTRIBUTE_KEY]), {
	message: CHANNEL_KEY_MESSAGE,
	path: [CHANNEL_ATTRIBUTE_KEY]
}).refine((attrs) => typeof attrs[CHANNEL_ATTRIBUTE_KEY] !== "string" || !isPrivateChannelKey(attrs[CHANNEL_ATTRIBUTE_KEY]), {
	message: MACHINE_PRINCIPAL_PRIVATE_CHANNEL_MESSAGE,
	path: [CHANNEL_ATTRIBUTE_KEY]
});
/**
* The body of `PATCH /internal/v1/machine-principals/{id}`. Each key is optional, and the body
* must name at least one; `{}` is refused (D-03).
*
* - `displayName`: 1 to {@link MACHINE_PRINCIPAL_DISPLAY_NAME_MAX_LENGTH} characters.
* - `attributes`: a patch over the stored map, see the attribute patch rule above.
* - `roles`: the whole new set of org role slugs, at most {@link MACHINE_PRINCIPAL_ROLES_MAX} slugs
*   of 1 to {@link MACHINE_PRINCIPAL_ROLE_SLUG_MAX_LENGTH} characters. The server replaces the set.
*   `[]` removes every role. core-api checks each slug the set adds, and only those, through
*   `assertRolesGrantable` (ruling R-012). `platform-agent-runtime` is operator-only: a set that
*   adds it to a row without it, or omits it from a row that holds it, is 403. A set that keeps
*   the row's presence or absence of that role passes this rule. For an `external` row, core-api's
*   external role guard decides the set too (ruling R-002).
* - `status`: one of {@link MACHINE_PRINCIPAL_SETTABLE_STATUSES}. `revoked` is refused here.
*
* Strict: an unknown key is refused; no body key sets `appSlug`, `agentId`, `type` or `orgId`. The
* at-least-one rule is a `.refine()`, which `createOpenApiDocument()` drops, so `openapi.json` shows
* four optional keys. Owner: WS-2's controller, through `@Body({ schema })`. Consumer: WS-6's
* `MachinePrincipalsClient.update`.
*/
const updateMachinePrincipalRequestSchema = z.strictObject({
	displayName: z.string().min(1).max(256).describe("New display name").optional(),
	attributes: machinePrincipalAttributesPatchSchema.describe("Attribute patch: a string sets a key, null deletes it, an absent key stays").optional(),
	roles: z.array(z.string().min(1).max(256)).max(64).describe("The whole new set of org role slugs").optional(),
	status: z.enum(MACHINE_PRINCIPAL_SETTABLE_STATUSES).describe("active or suspended").optional()
}).refine((body) => body.displayName !== void 0 || body.attributes !== void 0 || body.roles !== void 0 || body.status !== void 0, { message: "name at least one of displayName, attributes, roles and status" });
/** The body of `GET` and `PATCH /internal/v1/machine-principals/{id}`: the principal after the read or the write. */
const machinePrincipalResponseSchema = machinePrincipalRecordSchema;
/** The collection path of the admin surface. */
const MACHINE_PRINCIPALS_ADMIN_PATH = "/internal/v1/machine-principals";
/**
* `GET /internal/v1/machine-principals`: the principals of the caller's organization that the
* caller holds `machine_principal:manage` on, narrowed by {@link listMachinePrincipalsQuerySchema},
* as one {@link machinePrincipalPageSchema} page (I2). An on-behalf-of request is 403 (I1).
*
* core-api decides the manage set in the authorizer's order (ruling R-005):
*
* 1. A site role that satisfies `machine_principal:manage`: every row of the organization.
* 2. A token `scp` that does not satisfy the permission: an empty page.
* 3. Otherwise `resolveAccess`: every row for an `all` answer, so an org owner lists every
*    principal through the owner standing rule; else the granted ids.
*
* The list holds no `human` row on any arm (ruling R-004). The org-key arm for an ungoverned
* principal admits `GET` of that one row and adds no row to the list (ruling R-005).
*/
const LIST_MACHINE_PRINCIPALS_ROUTE = Object.freeze({
	method: "GET",
	path: MACHINE_PRINCIPALS_ADMIN_PATH
});
/**
* `GET /internal/v1/machine-principals/{id}`: one principal as {@link machinePrincipalRecordSchema}.
* `MachinePrincipalManageAuthorizer` decides:
*
* - 400 when the path id is not a lower-case UUID (ruling R-010).
* - 404 `machine principal not found` when the row is missing, in another organization, or a
*   `human` row, on every arm, the org-key arm for an ungoverned principal included (ruling R-004).
*   The 404 comes before any 403.
* - 403 without `machine_principal:manage` on the row. A caller with the org key
*   `platform:machine-principal:revoke` reads an ungoverned principal (ruling R-005).
* - 403 for an on-behalf-of request (I1, I2).
*/
const GET_MACHINE_PRINCIPAL_ROUTE = Object.freeze({
	method: "GET",
	path: `${MACHINE_PRINCIPALS_ADMIN_PATH}/{id}`
});
/**
* `PATCH /internal/v1/machine-principals/{id}`: apply {@link updateMachinePrincipalRequestSchema}
* and answer 200 with the saved {@link machinePrincipalRecordSchema}. The authorizer, the 404
* for a missing or `human` row, the 404 before 403 rule and the on-behalf-of 403 are the `GET`
* rules. Other answers:
*
* - 400: a body the schema refuses, a key of {@link MACHINE_PRINCIPAL_OPERATOR_ONLY_ATTRIBUTE_KEYS}
*   with a string or `null` (ruling R-010), or a path id that is not a lower-case UUID. The schema
*   refuses an attribute patch over its bound: more than {@link MACHINE_PRINCIPAL_PATCH_ATTRIBUTES_MAX}
*   entries, a key over {@link MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_KEY_MAX_LENGTH} characters or a value
*   over {@link MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_VALUE_MAX_LENGTH} characters (ruling R-029).
* - 403: the caller's own principal row is not `active`.
* - 403: a body that names a key other than `status`, from a caller that reaches the principal
*   through the org key `platform:machine-principal:revoke` alone, with no `machine_principal:manage`
*   (core-api `REVOKE_KEY_PATCH_STATUS_ONLY_MESSAGE`, ruling R-034); that key admits a status change.
* - 403: a role set that changes the presence of `platform-agent-runtime`, a role set that adds a
*   slug the caller may not confer, or a role the external role guard refuses for an `external` row.
* - 403: a role set that adds a slug to the caller's own row, where the path id equals the
*   caller's `sub` in lower case: `a machine principal may not add a role to itself` (core-api
*   `SELF_ROLE_ADD_MESSAGE`, ruling R-021). A role set that only removes roles from the caller's
*   own row passes.
* - 403: `status: 'suspended'` on an `external` row: `an external machine principal takes no
*   suspend; revoke it through v1/admin/machine-principals` (core-api
*   `EXTERNAL_PRINCIPAL_SUSPEND_MESSAGE`, ruling R-016, ruling R-017). An `external` principal is
*   `active` or `revoked`.
* - 403: `status: 'suspended'` on a row that holds `platform-agent-runtime`, for every caller:
*   `a machine principal that holds role "platform-agent-runtime" takes no suspend` (core-api
*   `AGENT_RUNTIME_PRINCIPAL_SUSPEND_MESSAGE`, ruling R-028). `status: 'active'` on such a row
*   passes.
* - 409: a principal whose status is `revoked`. A revoked principal takes no `PATCH`.
*
* `status: 'suspended'` keeps `k8s_subject` and the keys (I3). A `PATCH` that sets the status the
* row already holds changes nothing and answers 200. A status change writes the audit row
* `machine_principal.suspended` or `machine_principal.resumed`, and a changed role set writes
* `machine_principal.roles_changed`, in the same transaction (ruling R-003).
*/
const UPDATE_MACHINE_PRINCIPAL_ROUTE = Object.freeze({
	method: "PATCH",
	path: `${MACHINE_PRINCIPALS_ADMIN_PATH}/{id}`
});
/**
* The path of one principal, for `GET` and `PATCH`: {@link MACHINE_PRINCIPALS_ADMIN_PATH}, a slash
* and the id. Callers: WS-6's client and its fake, and WS-2's route tests.
*
* `principalId` must pass {@link isLowerCaseUuid}: a lower-case UUID with a version nibble of 1 to
* 8 and a variant nibble of 8, 9, a or b, or the nil or the max UUID. That is the only form
* core-api's authorizer admits for `{id}` (ruling R-010; dp-resource-grants R-016). The function
* throws `TypeError` for every other string: an upper-case or mixed-case UUID, a braced UUID, a
* version nibble of 0 or 9 to f, a variant nibble outside 8, 9, a and b, an agent slug, an empty
* string, and any string with a byte such as `..`, `/` or `?`. So a refused id never reaches a path
* and never costs a request. Pure.
*/
function machinePrincipalPath(principalId) {
	if (!isLowerCaseUuid(principalId)) throw new TypeError(`machinePrincipalPath: ${JSON.stringify(principalId)} is not a principal id`);
	return `${MACHINE_PRINCIPALS_ADMIN_PATH}/${principalId}`;
}
//#endregion
export { AGENT_HOST_ATTRIBUTE, AGENT_HOST_KINDS, AGENT_HOST_PRINCIPAL_ID_ATTRIBUTE, AGENT_ID_RESERVED_PATTERN, GET_MACHINE_PRINCIPAL_ROUTE, LIST_MACHINE_PRINCIPALS_ROUTE, MACHINE_PRINCIPALS_ADMIN_PATH, MACHINE_PRINCIPALS_CURSOR_MAX_LENGTH, MACHINE_PRINCIPALS_DEFAULT_PAGE, MACHINE_PRINCIPALS_MAX_PAGE, MACHINE_PRINCIPAL_AGENT_ID_MAX_LENGTH, MACHINE_PRINCIPAL_APP_SLUG_MAX_LENGTH, MACHINE_PRINCIPAL_DISPLAY_NAME_MAX_LENGTH, MACHINE_PRINCIPAL_OPERATOR_ONLY_ATTRIBUTE_KEYS, MACHINE_PRINCIPAL_PATCH_ATTRIBUTES_MAX, MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_KEY_MAX_LENGTH, MACHINE_PRINCIPAL_PATCH_ATTRIBUTE_VALUE_MAX_LENGTH, MACHINE_PRINCIPAL_PRIVATE_CHANNEL_MESSAGE, MACHINE_PRINCIPAL_ROLES_MAX, MACHINE_PRINCIPAL_ROLE_SLUG_MAX_LENGTH, MACHINE_PRINCIPAL_SETTABLE_STATUSES, MACHINE_PRINCIPAL_STATUSES, MACHINE_PRINCIPAL_TYPES, RESERVED_ATTRIBUTE_KEYS, RESERVED_ATTRIBUTE_KEY_MESSAGE, UPDATE_MACHINE_PRINCIPAL_ROUTE, agentHostKindOf, agentHostPrincipalIdOf, isLowerCaseUuid, isReservedAttributeKey, listMachinePrincipalsQuerySchema, machinePrincipalPageSchema, machinePrincipalPath, machinePrincipalRecordSchema, machinePrincipalResponseSchema, operatorOnlyAgentHostAttributesIn, updateMachinePrincipalRequestSchema };

//# sourceMappingURL=machine-principal.js.map